import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha, Chip} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { MovimentoService, IListagemMovimento } from '../../../shared/services/api/estoque/movimento/MovimentoService';
import dayjs from 'dayjs';
import { FerramentasDaListagemEntrada } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemEntrada';
import { Receipt as ReceiptIcon, Person as PersonIcon, CalendarToday as CalendarIcon, Assignment as AssignmentIcon } from '@mui/icons-material';


export const ListagemDeEntradas: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemMovimento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'entradas';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);

  // Altura fixa de uma linha: maior conteúdo da linha é o empilhamento valor+"Total" na
  // coluna Valor Total (duas linhas de texto), mais o padding vertical (py: 0.8 => 12.8px) e a
  // borda. Como nenhuma célula quebra em mais de duas linhas (todas as outras usam noWrap),
  // a altura real não varia com o conteúdo — por isso pode ser uma constante em vez de medida
  // no DOM. Medir uma linha real exigiria que as entradas já tivessem chegado da API,
  // obrigando uma primeira requisição "de calibração" com um limite ainda errado e uma
  // segunda corrigida.
  const ALTURA_LINHA_PADRAO = 44;

  const calcularLimite = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de "calc(100vh - Npx)",
    // que ficava desalinhado com a altura real do título/toolbar e causava scroll na página inteira.
    const BOTTOM_GAP = 16;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);

    // O <thead> é sempre renderizado, mesmo sem entradas carregadas, então dá para medi-lo
    // logo no mount — diferente da linha do corpo, ele não depende de dados terem chegado.
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 36;

    // Reservada como constante, nunca medida a partir do <tfoot>: a paginação só existe no
    // DOM quando totalCount > rowsPerPage, então medi-la criaria uma dependência circular
    // (ela aparece/desaparece conforme o próprio rowsPerPage muda).
    const FOOTER_RESERVADO = 56;
    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    const alturaDisponivel = alturaContainer - alturaHeader - FOOTER_RESERVADO;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / ALTURA_LINHA_PADRAO) - FOLGA_LINHAS)
    );

    setContainerHeight(prev => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect (não useEffect) para rodar de forma síncrona antes da pintura e, mais
  // importante, antes dos efeitos de busca (que dependem de rowsPerPage) dispararem a
  // requisição inicial — assim a primeira busca já sai com o limite correto, sem uma segunda
  // requisição de correção.
  useLayoutEffect(() => {
    calcularLimite();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  const dataInicial = useMemo(() => {
    return searchParams.get('datainicial') || dayjs().startOf('month').format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const dataFinal = useMemo(() => {
    return searchParams.get('datafinal') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);
  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);
  const idparceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);
  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }
  const [busca, setBusca] = useState(true);
  const [autoComplete, setAutoComplete] = useState(true);
  const [data, setData] = useState(true);
  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setBusca(false);
      setAutoComplete(false);
      setRows([]);
      setSearchParams({ tipo: 'data', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'parceiro') {
      setAutoComplete(true);
      setBusca(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'parceiro', pagina: '1' }, { replace: true });
      setTotalCount(0);
    } else {
      setBusca(true);
      setAutoComplete(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'numero', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
  }, [tipo]);
  useEffect(() => {
    if (tipo === 'numero' && filter != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    }
  }, [filter, pagina, rowsPerPage]);
  useEffect(() => {
    // O "else" só deve limpar a listagem quando o tipo ativo É "data" mas o filtro de datas
    // está inválido — nunca quando o tipo ativo é outro (numero/parceiro). Antes o "else" era
    // avaliado sempre que tipo !== 'data' também, então toda vez que rowsPerPage/pagina mudava
    // (independente do tipo selecionado), este efeito zerava a listagem por cima do resultado
    // que o efeito do tipo realmente ativo estava buscando — causando o "pisca" de duas listagens.
    if (tipo !== 'data') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    // Mesmo motivo do efeito acima: só limpa a listagem quando o tipo ativo É "parceiro"
    // mas o parceiro está vazio, nunca quando outro tipo está ativo.
    if (tipo !== 'parceiro') return;

    if (idparceiro != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);

            }
          });
      });
    } else {
      setRows([]);
    }
  }, [idparceiro, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({
        parceiro: idparceiro,
        filter: filter,
        tipo: tipo,
        datainicial: dataInicial,
        datafinal: dataFinal,
        pagina: '1'
      }, { replace: true });
    }
  }, [rowsPerPage]);
  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';
    const apenasNumeros = documento.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documento;
  };
  return (
    <>
     
      <LayoutBaseDePagina
        titulo='Listagem de Entradas'
        barraDeFerramentas={
          <FerramentasDaListagemEntrada
            mostrarInputBusca={busca}
            mostrarInputBasicSelect
            mostrarInputData={data}
            mostrarAutoComplete={autoComplete}
            dataInicial={dataInicial}
            dataFinal={dataFinal}
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Nova Entrada'
            aoClicarEmNovo={() => navigate('/entrada/novo')}
            aoMudarDataInicial={datainicial => setSearchParams({ tipo: tipo, pagina: '1', datainicial: datainicial, datafinal: dataFinal }, { replace: true })}
            aoMudarDataFinal={datafinal => setSearchParams({ tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: datafinal }, { replace: true })}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoParceiro={(texto, texto1) => setSearchParams({ parceiroselect: texto1, parceiro: texto, filter: filter, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
              { nameBD: 'parceiro', descricao: 'Parceiro' },
              { nameBD: 'numero', descricao: 'Número' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.9)
              : theme.palette.background.paper,
          }}
        >
          <TableContainer
            ref={tabelaRef}
            sx={{
              // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
              // última página parcial de uma listagem paginada), para o fundo acompanhar a
              // quantidade real de itens em vez de esticar até o fim da tela.
              height: rows.length >= rowsPerPage
                ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
                : 'auto',
              maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
              overflow: 'auto',
              scrollbarGutter: 'stable',
            }}
          >
            <Table size="small" sx={{ minWidth: '600px' }}>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.dark, 0.3)
                    : alpha(theme.palette.primary.main, 0.06),
                  '& th': {
                    fontWeight: 'bold',
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.primary.light
                      : theme.palette.primary.dark,
                    fontSize: '0.78rem',
                    py: 1,
                    px: 0.8,
                    borderBottom: `1px solid ${theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.light, 0.15)
                      : alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}>
                  <TableCell sx={{ width: '90px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <CalendarIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      <span>Data</span>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <PersonIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      <span>Parceiro</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '120px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <AssignmentIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      <span>Documento</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '90px' }}>
                    <Box display="flex" alignItems="center" gap={0.3}>
                      <ReceiptIcon fontSize="small" sx={{ fontSize: '16px' }} />
                      <span>Nº Nota</span>
                    </Box>
                  </TableCell>
                  <TableCell align='right' sx={{ width: '110px' }}>
                    <span>Valor Total</span>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.idmov}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? (index % 2 === 0
                          ? alpha(theme.palette.background.default, 0.4)
                          : alpha(theme.palette.background.default, 0.6))
                        : (index % 2 === 0
                          ? 'transparent'
                          : alpha(theme.palette.grey[50], 0.3)),
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.main, 0.12)
                          : alpha(theme.palette.primary.main, 0.03),
                      },
                      '&:last-child td': {
                        borderBottom: 0,
                      },
                      '& td': {
                        py: 0.8,
                        px: 0.8,
                        fontSize: '0.76rem',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }
                    }}
                    onClick={() => navigate(`/entrada/${row.idmov}`)}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <CalendarIcon color="action" fontSize="small" sx={{ fontSize: '14px' }} />
                        <Typography variant="body2" fontWeight="medium" color="text.primary" fontSize="0.76rem">
                          {dayjs(row.emissao).format('DD/MM/YYYY')}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" color="text.primary" noWrap fontSize="0.76rem">
                          {row.nome || 'Não informado'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="text.secondary" fontSize="0.76rem" noWrap>
                        {formatarDocumento(row.cpf || '')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.numero || 'N/A'}
                        size="small"
                        color="primary"
                        variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 'bold',
                          minWidth: '45px',
                          height: '20px',
                          fontSize: '0.7rem',
                          '& .MuiChip-label': {
                            px: 0.8
                          },
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.dark, 0.25)
                            : 'transparent'
                        }}
                      />
                    </TableCell>

                    <TableCell align='right'>
                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={row.total >= 0 ? 'success.main' : 'error.main'}
                          sx={{
                            fontSize: '0.78rem',
                            letterSpacing: '0.2px'
                          }}
                        >
                          {row.total.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                          Total
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {rows.length === 0 && isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                        <CircularProgress size={28} thickness={4} />
                        <Typography variant='body2' color='text.secondary' fontSize="0.75rem">
                          Carregando entradas...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
              {totalCount === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 3 }}>
                      <Box>
                        <ReceiptIcon sx={{
                          fontSize: 40,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.9rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.75rem">
                          {filter || idparceiro ? 'Tente ajustar os filtros de busca' : 'Nenhuma entrada encontrada no período'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              <TableFooter>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0 }}>
                      <LinearProgress
                        sx={{
                          height: '1.5px',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.dark, 0.2)
                            : undefined,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? theme.palette.primary.light
                              : undefined
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {(totalCount > 0 && totalCount > rowsPerPage) && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{
                      py: 1.5,
                      borderTop: `1px solid ${theme.palette.mode === 'dark'
                        ? alpha(theme.palette.divider, 0.2)
                        : alpha(theme.palette.divider, 0.1)}`,
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.7)
                        : alpha(theme.palette.grey[50], 0.2)
                    }}>
                      <Box display='flex' justifyContent='center' alignItems='center'>
                        <Pagination
                          page={pagina}
                          count={Math.ceil(totalCount / rowsPerPage)}
                          onChange={(_, newPage) => setSearchParams({
                            parceiro: idparceiro,
                            filter: filter,
                            tipo: tipo,
                            datainicial: dataInicial,
                            datafinal: dataFinal,
                            pagina: newPage.toString()
                          }, { replace: true })}
                          color='primary'
                          size="small"
                          shape='rounded'
                          showFirstButton
                          showLastButton
                          sx={{
                            '& .MuiPaginationItem-root': {
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                              fontSize: '0.75rem',
                              minWidth: '28px',
                              height: '28px',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.7)
                                : undefined,
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.25)
                                  : undefined
                              },
                              '&.Mui-selected': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.4)
                                  : undefined,
                                color: theme.palette.mode === 'dark'
                                  ? theme.palette.common.white
                                  : undefined
                              }
                            }
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};